import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../../api/client';
import { useApi } from '../../api/useApi';
import { ApplicationDetail } from '../evaluatorData';
import ApplicationDetailPanel from './ApplicationDetailPanel';

const SCORES = [1, 2, 3, 4, 5];

export default function ScoreApplicant() {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const id = appId ?? '';

  const { data: assigned, loading: assignedLoading, error: assignedError } = useApi(() => API.fiAssigned());
  const { data: detail, loading: detailLoading, error: detailError } = useApi<ApplicationDetail>(
    () => API.fiApplication(id),
    [id],
  );
  // This FI's previously-saved evaluation (draft or submitted) for rehydration.
  const { data: saved } = useApi<any>(() => API.fiScores(id), [id]);

  const [scores, setScores] = useState<Record<number, number>>({});
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [savedDraft, setSavedDraft] = useState(false);

  // Preload saved scores/notes/status once the GET resolves (keys arrive as strings).
  useEffect(() => {
    if (!saved) return;
    const restored: Record<number, number> = {};
    Object.entries(saved.scores ?? {}).forEach(([k, v]) => { restored[Number(k)] = Number(v); });
    setScores(restored);
    setNotes(saved.notes ?? '');
    setSavedDraft(saved.status === 'draft');
    setSubmitted(saved.status === 'submitted');
  }, [saved]);

  const average = useMemo(() => {
    const scored = Object.values(scores).filter(v => v > 0);
    if (scored.length === 0) return null;
    return scored.reduce((a, b) => a + b, 0) / scored.length;
  }, [scores]);

  const setScore = (qid: number, val: number) => {
    setScores(prev => ({ ...prev, [qid]: val }));
    setSavedDraft(false);
    setSubmitted(false);
  };

  const payload = () => ({ scores, notes, average });
  const onSaveDraft = () => { API.saveFiScores(id, { ...payload(), status: 'draft' }).catch(() => {}); setSavedDraft(true); };
  const onSubmit = () => {
    if (!allAnswered) return;
    API.saveFiScores(id, { ...payload(), status: 'submitted' }).catch(() => {});
    setSubmitted(true);
  };

  if (assignedLoading || detailLoading) return <div className="phil-loading">Loading…</div>;
  if (assignedError || detailError || !assigned || !detail) {
    return <div className="phil-error">Unable to load application. {assignedError ?? detailError}</div>;
  }

  const applicants: any[] = assigned.applicants;
  const evaluator: any = assigned.evaluator;
  const questions: any[] = assigned.questions;
  const allAnswered = questions.length > 0 && questions.every((q: any) => (scores[q.id] ?? 0) >= 1);
  const idx = applicants.findIndex((a: any) => a.id === id);
  const goTo = (delta: number) => {
    const next = applicants[idx + delta];
    if (next) navigate(`/evaluate/scoring/${next.id}`);
  };

  return (
    <>
      <div className="score-layout">
        {/* LEFT — evaluation form */}
        <div className="score-form">
          <div className="score-strip">
            <div>
              <button className="phil-btn-link score-back" onClick={() => navigate('/evaluate')}>← All Applicants</button>
              <div className="score-company">{detail.company}</div>
              <div className="score-company-sub">{detail.tagline}</div>
            </div>
            <div className="score-evaluator">
              <div className="lbl">Evaluator</div>
              <div className="name">{evaluator.name} · {evaluator.org}</div>
              <div className="meta">
                {id} · {evaluator.cycle} &nbsp;·&nbsp; {idx >= 0 ? idx + 1 : 1} of {evaluator.assignedTotal} assigned
              </div>
            </div>
          </div>

          <div className="section-label" style={{ color: '#1A2744', marginBottom: 4 }}>Evaluation Questions</div>
          <div className="phil-card" style={{ padding: '4px 14px' }}>
            {questions.map((q: any, idx: number) => (
              <div key={q.id} className="score-q">
                <div className="score-q-title">{idx + 1}. {q.title}</div>
                <div className="score-q-prompt">{q.prompt}</div>
                <div className="score-dots">
                  {SCORES.map(val => (
                    <span
                      key={val}
                      className={`score-dot${scores[q.id] === val ? ' selected' : ''}`}
                      onClick={() => setScore(q.id, val)}
                    >
                      {val}
                    </span>
                  ))}
                </div>
                <div className="score-legend">{q.legend}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 14 }}>
            <div className="section-label" style={{ color: '#1A2744' }}>Notes (optional)</div>
            <textarea
              className="phil-textarea"
              value={notes}
              onChange={e => { setNotes(e.target.value); setSavedDraft(false); }}
              placeholder="Add context for your scores…"
            />
          </div>

          <div className="score-actions">
            <div className="score-avg">My average: <strong>{average != null ? average.toFixed(1) : '—'}</strong></div>
            <div style={{ display: 'flex', gap: 8 }}>
              {!submitted && (
                <button className="phil-btn phil-btn-secondary phil-btn-sm" onClick={onSaveDraft}>
                  {savedDraft ? '✓ Saved' : 'Save Draft'}
                </button>
              )}
              <button
                className="phil-btn phil-btn-primary phil-btn-sm"
                onClick={onSubmit}
                disabled={!allAnswered && !submitted}
                title={!allAnswered ? 'Score all questions (1–5) before submitting' : undefined}
              >
                {submitted ? '✓ Submitted' : '✓ Submit'}
              </button>
            </div>
          </div>

          {submitted && (
            <div className="phil-saved-banner">✓ Evaluation submitted for {detail.company}</div>
          )}

          <div className="score-prevnext">
            <button className="phil-btn phil-btn-secondary phil-btn-sm" disabled={idx <= 0} onClick={() => goTo(-1)}>← Previous</button>
            <button className="phil-btn phil-btn-secondary phil-btn-sm" disabled={idx >= applicants.length - 1} onClick={() => goTo(1)}>Next →</button>
          </div>
        </div>

        {/* RIGHT — read-only application detail */}
        <div className="score-detail">
          <ApplicationDetailPanel appId={id} detail={detail} />
        </div>
      </div>
    </>
  );
}
